/**
 * Invoice status presentation — ONE shared vocabulary for every status
 * render (invoice workflow pass, founder feedback 2026-07-10:
 * "approved should be capitalized, and what other statuses are there /
 * what triggers each").
 *
 * Statuses are the schema CHECK's four: draft | approved | issued |
 * void. Labels are Title Case. Chip variants follow the design law
 * (color is meaning; gold means one thing, awaiting your confirm):
 *   draft    neutral  working state, reads in words
 *   approved caution  awaiting the owner's issue
 *   issued   affirm   settled and final
 *   void     neutral  a recorded fact, not an alarm
 *
 * OWNER-FACING surface: 'Void' is allowed vocabulary here (the invoice
 * pages are view_invoice-gated); it stays out of staff-facing copy.
 */

import type { ChipVariant } from '@/components/primitives/Chip';

export const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  approved: 'Approved',
  issued: 'Issued',
  void: 'Void',
};

/** Title Case label for a wire status; unknown values capitalize plainly. */
export function invoiceStatusLabel(status: string): string {
  const known = INVOICE_STATUS_LABELS[status];
  if (known) return known;
  return status.length === 0 ? status : status[0]!.toUpperCase() + status.slice(1);
}

export function invoiceStatusVariant(status: string): ChipVariant {
  switch (status) {
    case 'approved':
      return 'caution';
    case 'issued':
      return 'affirm';
    default:
      return 'neutral';
  }
}

/**
 * The list page's status legend (owner-facing; one line per status,
 * what it is + what moves it).
 */
export const INVOICE_STATUS_LEGEND: ReadonlyArray<{ status: string; meaning: string }> = [
  {
    status: 'draft',
    meaning:
      "Compiled and still open. Lines refresh from the month's ready notes; approve it when it reads right.",
  },
  { status: 'approved', meaning: 'Reviewed and locked for issue. Reopen returns it to draft.' },
  // "Sent to the client" was the QuickBooks column's fact to state, and
  // half the time it was not yet true. Issuing finalizes and numbers;
  // delivery is what the column reports (TRUTH-FIX train, 2026-08-31).
  {
    status: 'issued',
    meaning: 'Final and numbered. Whether it reached the client depends on the QuickBooks column.',
  },
  // A void happens IN OLIVE: the push is create-only (ruled #90), so a
  // copy already in QuickBooks stays live until voided there.
  {
    status: 'void',
    meaning:
      'Cancelled in Olive after issue. Its notes return to billable. A copy in QuickBooks is not cancelled by this.',
  },
];

/**
 * The QuickBooks column's legend (TRUTH-FIX train, 2026-08-31): one
 * plain sentence per state the column renders, keyed by the label the
 * column uses. The sentences restate the registries' rulings
 * (qboPushStatus.ts / qboCopyStatus.ts) at legend altitude; the ruled
 * operator copy itself stays in the cells and tooltips. The split that
 * must survive any edit: "Olive looked and it was not there" is never
 * the same entry as "Olive could not look".
 */
export const QBO_COLUMN_LEGEND: ReadonlyArray<{ label: string; meaning: string }> = [
  { label: 'In QuickBooks', meaning: 'Sent, and the total there matched when Olive checked.' },
  {
    label: 'Total unverified',
    meaning: 'Sent, but Olive could not read the total back; check it in QuickBooks.',
  },
  {
    label: 'Total differs',
    meaning:
      'Sent, but the total in QuickBooks does not match what Olive froze; compare before it goes to the family.',
  },
  {
    label: 'Not found in QuickBooks',
    meaning: 'Olive looked at the shown time and no copy was there.',
  },
  { label: 'Rate band not mapped', meaning: 'Nothing was sent; map the rate band, then send again.' },
  { label: 'Sending', meaning: 'A send is in flight; nothing to do yet.' },
  {
    label: 'Could not check',
    meaning: 'Olive could not reach QuickBooks, which is not the same as the copy not being there.',
  },
  { label: '–', meaning: 'Not sent yet; drafts and approved invoices have no copy.' },
];
